import type {
  FetchOrgScheduleQuestionnairesArgs,
  UpdateScheduleQuestionnaireArgs,
  CreateScheduleQuestionnaireArgs,
  FetchScheduleQuestionnairesArgs,
} from './types/scheduleQuestionnaire.types';
import type {
  ApiScheduleQuestionnaire,
  ApiScheduleQuestionnaireRow,
  UiScheduleQuestionnaire,
} from '../schemas/scheduleQuestionnaire.schema';
import type { BaseApiResponse } from './types/api.types';

import {
  updateScheduleQuestionnaireApiArgs,
  createScheduleQuestionnaireApiArgs,
  mapScheduleQuestionnaireFromApi,
} from './mappers/scheduleQuestionnaire.mappers';
import { destroy, patch, post, get } from './axios';

export const createScheduleQuestionnaire = async (
  args: CreateScheduleQuestionnaireArgs,
) => {
  const response = await post<ApiScheduleQuestionnaireRow[]>(
    '/v2/schedule-questionnaires',
    createScheduleQuestionnaireApiArgs(args),
  );
  return response?.data ?? null;
};

export const fetchScheduleQuestionnaires = async ({
  clientId,
  from,
  organizationId,
  to,
}: FetchScheduleQuestionnairesArgs) => {
  const params = new URLSearchParams({ clientId, from, to });
  // The BE authorizes the read against this organization — omitting it made
  // every org-context request fall back to the caller's last used org.
  if (organizationId) params.set('organizationId', organizationId);
  const response = await get<BaseApiResponse<ApiScheduleQuestionnaire[]>>(
    `/v2/schedule-questionnaires?${params.toString()}`,
  );
  return {
    ...response,
    data: response?.data?.map(
      mapScheduleQuestionnaireFromApi,
    ) as UiScheduleQuestionnaire[],
  };
};

export const fetchOrgScheduleQuestionnaires = async ({
  from,
  organizationId,
  to,
}: FetchOrgScheduleQuestionnairesArgs) => {
  const response = await get<BaseApiResponse<ApiScheduleQuestionnaire[]>>(
    `/v2/organizations/${organizationId}/schedule-questionnaires?from=${from}&to=${to}`,
  );
  return {
    ...response,
    data: response?.data?.map(
      mapScheduleQuestionnaireFromApi,
    ) as UiScheduleQuestionnaire[],
  };
};

export const updateScheduleQuestionnaire = async ({
  id,
  ...rest
}: UpdateScheduleQuestionnaireArgs) => {
  const response = await patch<ApiScheduleQuestionnaireRow>(
    `/v2/schedule-questionnaires/${id}`,
    updateScheduleQuestionnaireApiArgs(rest),
  );
  return response?.data ?? null;
};

export const deleteScheduleQuestionnaire = async (id: string) => {
  await destroy(`/v2/schedule-questionnaires/${id}`);
};

export const deleteFutureScheduleQuestionnaires = async (id: string) => {
  await destroy(`/v2/schedule-questionnaires/${id}/future-series`);
};
